// Life & Wealth Solutions - Phase 5: Enhanced Agent Portal
// Phoenix Rescue Apps - "Your Agency to AI Today"
// Unified Portal integrating all Phase 4 & Phase 5 features

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, Target, Users, TrendingUp, Calendar, Phone,
  CheckCircle, AlertTriangle, DollarSign, Clock, Zap,
  Bell, Award, ArrowRight, RefreshCw, Brain, Activity
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const EnhancedAgentPortal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activeAutomations, setActiveAutomations] = useState(0);
  const [performanceData, setPerformanceData] = useState(null);

  useEffect(() => {
    fetchAgentData();
  }, []);

  const fetchAgentData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Fetch data from all Phase 4 services
      const [dashboard, churn, opportunities, notifs, triggers, performance] = await Promise.all([
        axios.get(`${API_URL}/analytics/dashboard?days=30`, config),
        axios.get(`${API_URL}/predictive/churn/high-risk`, config),
        axios.get(`${API_URL}/predictive/opportunities/top?limit=5`, config),
        axios.get(`${API_URL}/notifications?limit=5`, config),
        axios.get(`${API_URL}/automation/triggers`, config),
        axios.get(`${API_URL}/analytics/performance?days=30`, config)
      ]);

      setDashboardData(dashboard.data);
      setPredictions({
        churnRisks: churn.data.customers || [],
        opportunities: opportunities.data.opportunities || []
      });
      setNotifications(notifs.data.notifications || []);
      setActiveAutomations(triggers.data.triggers?.filter(t => t.is_active).length || 0);
      setPerformanceData(performance.data);
    } catch (error) {
      console.error('Failed to fetch agent data:', error);
      setDemoData();
    } finally {
      setLoading(false);
    }
  };

  const setDemoData = () => {
    setDashboardData({
      metrics: {
        total_leads: 245,
        active_leads: 87,
        conversion_rate: 28.5,
        total_appointments: 42,
        completed_appointments: 38,
        appointment_completion_rate: 90.5,
        total_tasks: 156,
        completed_tasks: 134,
        task_completion_rate: 85.9,
        total_calls: 234,
        successful_calls: 167,
        call_success_rate: 71.4,
        revenue_current: 127500,
        revenue_growth: 18.3
      },
      ai_insights: [
        'Your conversion rate is 12% above team average',
        'Best performance on Thursday appointments (95% completion)',
        'Email follow-ups generating highest engagement'
      ],
      performance_score: 89
    });

    setPredictions({
      churnRisks: [
        {
          customer_id: 'C2001',
          name: 'Global Insurance Co',
          risk_score: 0.82,
          policy_value: 125000
        }
      ],
      opportunities: [
        {
          opportunity_id: 'O3001',
          name: 'Enterprise Life Package',
          customer: 'TechCorp Industries',
          score: 94,
          value: 245000,
          probability: 0.89
        },
        {
          opportunity_id: 'O3002',
          name: 'Executive Health Plan',
          customer: 'Finance Group LLC',
          score: 87,
          value: 178000,
          probability: 0.76
        }
      ]
    });

    setNotifications([
      {
        notification_id: 'N1001',
        type: 'success',
        title: 'Payment Received',
        message: 'Payment of $2,500 received from Global Insurance Co',
        created_at: new Date().toISOString()
      },
      {
        notification_id: 'N1002',
        type: 'info',
        title: 'New Lead Assigned',
        message: 'You have been assigned lead: John Anderson',
        created_at: new Date().toISOString()
      }
    ]);

    setActiveAutomations(8);

    setPerformanceData({
      overall_score: 89,
      trends: [
        { date: '2025-12-04', score: 84 },
        { date: '2025-12-05', score: 86 },
        { date: '2025-12-06', score: 87 },
        { date: '2025-12-07', score: 88 },
        { date: '2025-12-08', score: 89 }
      ]
    });
  };

  const QuickActionCard = ({ icon: Icon, title, description, onClick, color = 'blue' }) => (
    <button
      onClick={onClick}
      className={`bg-white rounded-lg shadow border border-gray-200 p-6 hover:shadow-lg transition-all text-left w-full`}
    >
      <div className="flex items-center space-x-4">
        <div className={`p-3 bg-${color}-50 rounded-lg`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
        <ArrowRight className="h-5 w-5 text-gray-400" />
      </div>
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Agent Portal</h1>
          <p className="text-gray-500 mt-1">Welcome back! Here's your performance overview.</p>
        </div>
        <button
          onClick={fetchAgentData}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Performance Score Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold mb-2">Performance Score</h2>
            <div className="flex items-end space-x-2">
              <span className="text-5xl font-bold">{performanceData?.overall_score || 0}</span>
              <span className="text-2xl mb-2">/100</span>
            </div>
            <div className="mt-4 space-y-1">
              {dashboardData?.ai_insights?.slice(0, 2).map((insight, idx) => (
                <div key={idx} className="flex items-center space-x-2 text-blue-100">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm">{insight}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="w-48 h-32">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={performanceData?.trends || []}>
                <Line type="monotone" dataKey="score" stroke="#ffffff" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Leads</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.metrics.active_leads || 0}</p>
              <p className="text-xs text-green-600 mt-1">↑ {dashboardData?.metrics.conversion_rate || 0}% conversion</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Appointments</p>
              <p className="text-2xl font-bold text-gray-900">{dashboardData?.metrics.total_appointments || 0}</p>
              <p className="text-xs text-green-600 mt-1">
                {dashboardData?.metrics.appointment_completion_rate || 0}% completion
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <Calendar className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Tasks Completed</p>
              <p className="text-2xl font-bold text-gray-900">
                {dashboardData?.metrics.completed_tasks || 0}/{dashboardData?.metrics.total_tasks || 0}
              </p>
              <p className="text-xs text-green-600 mt-1">
                {dashboardData?.metrics.task_completion_rate || 0}% rate
              </p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg">
              <CheckCircle className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                ${(dashboardData?.metrics.revenue_current || 0).toLocaleString()}
              </p>
              <p className="text-xs text-green-600 mt-1">
                ↑ {dashboardData?.metrics.revenue_growth || 0}% growth
              </p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <QuickActionCard
            icon={BarChart3}
            title="View Analytics"
            description="Deep dive into your performance metrics"
            onClick={() => navigate('/analytics-enhanced')}
            color="blue"
          />
          <QuickActionCard
            icon={Brain}
            title="AI Predictions"
            description="Check lead scores and opportunities"
            onClick={() => navigate('/predictions')}
            color="purple"
          />
          <QuickActionCard
            icon={Zap}
            title="Automation Builder"
            description={`${activeAutomations} active automations`}
            onClick={() => navigate('/automation-builder')}
            color="yellow"
          />
          <QuickActionCard
            icon={Bell}
            title="Notifications"
            description={`${notifications.length} unread messages`}
            onClick={() => navigate('/notifications-center')}
            color="red"
          />
          <QuickActionCard
            icon={Users}
            title="Lead Management"
            description="Manage your leads pipeline"
            onClick={() => navigate('/leads')}
            color="green"
          />
          <QuickActionCard
            icon={Calendar}
            title="Appointments"
            description="View and schedule meetings"
            onClick={() => navigate('/appointments')}
            color="indigo"
          />
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* High Priority Predictions */}
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Top Opportunities</h2>
            <button
              onClick={() => navigate('/predictions')}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              View All
            </button>
          </div>
          <div className="space-y-3">
            {predictions?.opportunities?.slice(0, 3).map((opp) => (
              <div
                key={opp.opportunity_id}
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors cursor-pointer"
                onClick={() => navigate('/predictions')}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{opp.name}</h3>
                    <p className="text-sm text-gray-500">{opp.customer}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">{opp.score}</p>
                      <p className="text-xs text-gray-500">Score</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Value: ${opp.value.toLocaleString()}</span>
                  <span className="text-green-600 font-medium">
                    {(opp.probability * 100).toFixed(0)}% probability
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Notifications */}
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Recent Notifications</h2>
            <button
              onClick={() => navigate('/notifications-center')}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              View All
            </button>
          </div>
          <div className="space-y-3">
            {notifications.slice(0, 4).map((notification) => (
              <div
                key={notification.notification_id}
                className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                onClick={() => navigate('/notifications-center')}
              >
                <div className={`p-2 rounded-lg ${
                  notification.type === 'success' ? 'bg-green-100' :
                  notification.type === 'warning' ? 'bg-yellow-100' :
                  notification.type === 'alert' ? 'bg-red-100' :
                  'bg-blue-100'
                }`}>
                  {notification.type === 'success' ? <CheckCircle className="h-4 w-4 text-green-600" /> :
                   notification.type === 'warning' ? <AlertTriangle className="h-4 w-4 text-yellow-600" /> :
                   notification.type === 'alert' ? <AlertTriangle className="h-4 w-4 text-red-600" /> :
                   <Bell className="h-4 w-4 text-blue-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {notification.title}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {notification.message}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Churn Risk Alert */}
      {predictions?.churnRisks?.length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-600 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-medium text-red-900">
                High Churn Risk Alert
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  {predictions.churnRisks.length} customer{predictions.churnRisks.length > 1 ? 's' : ''} showing signs of churn risk.
                  Total at-risk value: ${predictions.churnRisks.reduce((sum, c) => sum + c.policy_value, 0).toLocaleString()}
                </p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/predictions')}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                >
                  View Details & Take Action
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity Stats */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">This Week's Activity</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <Phone className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{dashboardData?.metrics.total_calls || 0}</p>
            <p className="text-sm text-gray-600">Calls Made</p>
            <p className="text-xs text-green-600 mt-1">
              {dashboardData?.metrics.call_success_rate || 0}% success
            </p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <Target className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{dashboardData?.metrics.total_leads || 0}</p>
            <p className="text-sm text-gray-600">Leads</p>
            <p className="text-xs text-green-600 mt-1">
              {dashboardData?.metrics.conversion_rate || 0}% converted
            </p>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <Clock className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{dashboardData?.metrics.total_tasks || 0}</p>
            <p className="text-sm text-gray-600">Tasks</p>
            <p className="text-xs text-green-600 mt-1">
              {dashboardData?.metrics.task_completion_rate || 0}% complete
            </p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <Zap className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{activeAutomations}</p>
            <p className="text-sm text-gray-600">Active Automations</p>
            <p className="text-xs text-green-600 mt-1">Working 24/7</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedAgentPortal;
